import { Grid } from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DatePicker from 'react-datepicker';

import { RootState } from 'common/store/AppStore';
import {
	setDueDateValue,
	setEffectiveDateValue,
	setSelectedDueDate,
	setSelectedEffectiveDate,
} from 'common/store/reducer/horizonScanReducer';
import { formatDate, setDateRange } from 'common/utils/utilFunctions';

import 'react-datepicker/dist/react-datepicker.css';

import {
	DATE_DURATION,
	DATE_ENTITY,
} from 'features/HorizonScanning/HorizonScanConstants';

import {
	dispatchEndDateBasedOnTitle,
	dispatchStartDateBasedOnTitle,
} from './RangeDatePickerCompUtils';

import styles from './RangeDatePickerComp.module.scss';

interface IRangeDateProps {
	title: string;
	onDateRangeChange: (hasDateRange: boolean) => void;
	data: any;
	onSelectDateRange: (
		data: any,
		option: any,
		isAllOptionSelected?: boolean,
	) => void;
}

const { DUE_DATE, EFFECTIVE_DATE } = DATE_ENTITY;

const CustomDateRangeComponent = (props: IRangeDateProps) => {
	const { title, onSelectDateRange, data } = props;
	const dispatch = useDispatch();

	/* Local states */
	const [customValue, setCustomValue] = useState<any>(null);

	/* Stores variables */
	const dateArray = useSelector(
		(state: RootState) => state.horizonScan.selectedDateArray,
	);

	const dueStartDate = useSelector(
		(state: RootState) => state.horizonScan.dueStartDate,
	);

	const dueEndDate = useSelector(
		(state: RootState) => state.horizonScan.dueEndDate,
	);

	const effectiveStartDate = useSelector(
		(state: RootState) => state.horizonScan.effectiveStartDate,
	);

	const effectiveEndDate = useSelector(
		(state: RootState) => state.horizonScan.effectiveEndDate,
	);

	const selectedDateOption = useSelector((state: RootState) =>
		title?.toLowerCase() === DUE_DATE?.toLowerCase()
			? state.horizonScan.selectedDueDate
			: state.horizonScan.selectedEffectiveDate,
	);

	/* Local Variables */
	const startDateData =
		title?.toLowerCase() === DUE_DATE?.toLowerCase()
			? dueStartDate
			: effectiveStartDate;

	const endDateData =
		title?.toLowerCase() === DUE_DATE?.toLowerCase()
			? dueEndDate
			: effectiveEndDate;

	const options = [
		DATE_DURATION.ALL,
		DATE_DURATION.THIS_WEEK,
		DATE_DURATION.THIS_MONTH,
		DATE_DURATION.LAST_THREE_MONTHS,
		DATE_DURATION.LAST_SIX_MONTHS,
		DATE_DURATION.THIS_YEAR,
		DATE_DURATION.CUSTOM_DATES,
	];

	const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const option = event.target.value;

		if (title?.toLowerCase() === DUE_DATE?.toLowerCase()) {
			dispatch(setSelectedDueDate(option));
		} else {
			dispatch(setSelectedEffectiveDate(option));
		}

		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth();
		const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

		if (option?.toLowerCase() !== DATE_DURATION.CUSTOM_DATES?.toLowerCase()) {
			const { startDate, endDate } = setDateRange(
				option,
				currentDate,
				currentYear,
				currentMonth,
				lastDayOfMonth,
			);
			const formattedStartDate = formatDate(startDate);
			const formattedEndDate = formatDate(endDate);
			const isAllOptionSelected =
				option?.toLowerCase() === DATE_DURATION.ALL.toLowerCase();
			onSelectDateRange(
				[formattedStartDate, formattedEndDate],
				data,
				isAllOptionSelected,
			);
		}
	};

	useEffect(() => {
		if (dateArray.length !== 0) {
			setCustomValue(dateArray);
		} else {
			setCustomValue(null);
		}
	}, [dateArray]);

	useEffect(() => {
		if (selectedDateOption !== 'All' && customValue !== null) {
			if (title?.toLowerCase() === DUE_DATE?.toLowerCase()) {
				dispatch(setDueDateValue(customValue));
			} else if (title?.toLowerCase() === EFFECTIVE_DATE?.toLowerCase()) {
				dispatch(setEffectiveDateValue(customValue));
			}
		}
	}, [customValue]);

	return (
		<div>
			{options.map((option) => (
				<div key={option} className={styles['range-date-picker-row-padding']}>
					<input
						type="radio"
						id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
						value={option}
						checked={
							selectedDateOption === option ||
							(option === DATE_DURATION.ALL && selectedDateOption === '')
						}
						onChange={handleOptionChange}
						className={styles['range-date-picker-input-styles']}
					/>
					<label
						htmlFor={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
						className={styles['range-date-picker-label-value']}
					>
						{option}
					</label>
				</div>
			))}
			{selectedDateOption === 'Custom dates' && (
				<div>
					<Grid
						container
						className={styles['range-date-picker-container-styles']}
					>
						<Grid
							item
							className={styles['range-date-picker-label-value-start-date']}
						>
							Start Date:
						</Grid>
						<Grid item>
							<DatePicker
								selected={startDateData}
								onChange={(date) => {
									dispatchStartDateBasedOnTitle(title, date, dispatch);
									if (date && endDateData) {
										onSelectDateRange(
											[formatDate(date), formatDate(endDateData)],
											data,
										);
									}
								}}
								selectsStart
								startDate={startDateData}
								endDate={endDateData}
								dateFormat="MM/dd/yyyy"
								calendarClassName={styles['custom-datepicker']}
								wrapperClassName={styles.datePicker}
								placeholderText="MM/DD/YYYY"
								popperClassName={styles['range-date-picker-popper']}
							/>
						</Grid>
					</Grid>
					<Grid
						container
						className={styles['range-date-picker-container-styles']}
					>
						<Grid
							item
							className={styles['range-date-picker-label-value-end-date']}
						>
							End Date:
						</Grid>
						<Grid item>
							<DatePicker
								selected={endDateData}
								onChange={(date) => {
									dispatchEndDateBasedOnTitle(title, date, dispatch);
									if (date && startDateData) {
										onSelectDateRange(
											[formatDate(startDateData), formatDate(date)],
											data,
										);
									}
								}}
								selectsEnd
								startDate={startDateData}
								endDate={endDateData}
								minDate={startDateData}
								dateFormat="MM/dd/yyyy"
								placeholderText="MM/DD/YYYY"
								popperClassName={styles['range-date-picker-popper']}
							/>
						</Grid>
					</Grid>
				</div>
			)}
		</div>
	);
};

export default CustomDateRangeComponent;
